import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { normalizeEmail, OTP_RESEND_COOLDOWN_SECONDS, WEB_ROUTES } from '@portal-contract';
import { Btn } from '@/components/Btn';
import { api } from '@/api/client';
import { ApiClientError } from '@/api/errors';
import { useI18n } from '@/i18n/I18nProvider';
import { useSession, defaultDestination } from '@/session/SessionProvider';
import { useLoginFlow } from '@/session/LoginFlow';
import { Field } from '@/components/Field';
import { PageHeading } from '@/components/PageHeading';
import { Card } from '@/components/Card';
import { errorMessage } from '@/components/States';

/** `/login`: one field. The copy never says whether the address is registered (plan 4.3). */
export function LoginScreen() {
  const { t, lang } = useI18n();
  const session = useSession();
  const flow = useLoginFlow();
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState(flow.email ?? '');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // A visitor who already has a live session does not need the form.
  useEffect(() => {
    if (session.status === 'authenticated') navigate(defaultDestination(session.customers), { replace: true });
  }, [session.status, session.customers, navigate]);

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (pending) return;
    const normalized = normalizeEmail(email);
    if (!normalized) {
      setError(t('login.invalidEmail'));
      inputRef.current?.focus();
      return;
    }
    setError(null);
    setPending(true);
    try {
      const sent = await api.requestCode(normalized, lang);
      flow.start(normalized, sent.resendAfterSeconds);
      navigate(WEB_ROUTES.loginCode, { state: location.state });
    } catch (err) {
      if (session.handleSessionError(err)) return;
      if (err instanceof ApiClientError && err.code === 'invalid_email') setError(t('login.invalidEmail'));
      else if (err instanceof ApiClientError && err.code === 'rate_limited' && err.retryAfterSeconds && err.retryAfterSeconds <= OTP_RESEND_COOLDOWN_SECONDS) {
        // The per-address resend cooldown (a longer wait is another limit): the visitor probably still holds a valid code: the message points at "I already have a code".
        setError(t('login.cooldown', { seconds: err.retryAfterSeconds }));
      } else {
        const m = errorMessage(err);
        setError(t(m.key, m.vars));
      }
      inputRef.current?.focus();
    } finally {
      setPending(false);
    }
  }

  /** No request is made: verifying needs only the challenge cookie and the code. */
  function haveCode() {
    flow.startCodeOnly();
    navigate(WEB_ROUTES.loginCode, { state: location.state });
  }

  return (
    <div className="mx-auto w-full max-w-[440px]" data-screen="LoginScreen">
      <PageHeading focus={false}>{t('login.heading')}</PageHeading>
      <p className="text-body text-typography-secondary mt-200">{t('login.intro')}</p>
      <Card className="mt-500 p-400 sm:p-500">
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-400">
          <Field
            ref={inputRef}
            label={t('login.emailLabel')}
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            maxLength={254}
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            error={error}
          />
          <Btn type="submit" size="lg" className="min-h-[48px] w-full" loading={pending}>
            {t('login.submit')}
          </Btn>
        </form>
        <div className="mt-300">
          <Btn type="button" variant="link" className="min-h-[44px]" onClick={haveCode}>
            {t('login.haveCode')}
          </Btn>
        </div>
      </Card>
    </div>
  );
}
